import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { GoldButton } from "@/components/site/Ui";
import Logo from "@/components/site/Logo";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ClipboardList } from "lucide-react";

const PLATFORMS = ["YouTube", "TikTok", "Instagram", "Facebook", "Other"];
const LEVELS = ["Complete beginner", "Some experience", "Confident but want to grow"];
const YESNO = ["Yes", "No", "Not sure yet"];

const FIELDS = [
  { name: "full_name", label: "Full name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "whatsapp", label: "WhatsApp number" },
  { name: "platform", label: "Main social platform", type: "select", options: PLATFORMS, required: true },
  { name: "account_link", label: "Account link (if any)" },
  { name: "niche", label: "Niche / interests", type: "textarea", required: true, placeholder: "What topics excite you?" },
  { name: "experience", label: "Current experience level", type: "select", options: LEVELS, required: true },
  { name: "goals", label: "What you want to achieve", type: "textarea", required: true },
  { name: "audience", label: "Target audience", placeholder: "Who is your content for?" },
  { name: "schedule", label: "Your job / daily schedule", type: "textarea" },
  { name: "hours", label: "Hours available per week" },
  { name: "lifestyle", label: "Lifestyle notes", type: "textarea" },
  { name: "on_camera", label: "Comfortable showing your face on camera?", type: "select", options: YESNO, required: true },
  { name: "devices", label: "Devices you own", placeholder: "e.g. iPhone, iPad" },
  { name: "languages", label: "Languages you'll create in" },
  { name: "styles_like", label: "Content styles you like" },
  { name: "styles_dislike", label: "Content styles you dislike" },
  { name: "creators", label: "Example creators you admire", type: "textarea" },
  { name: "anything_else", label: "Anything else?", type: "textarea" },
];

export default function Questionnaire() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/questionnaire/${token}`)
      .then((r) => {
        setMeta(r.data);
        setValues({ full_name: r.data.customer_name || "", email: r.data.customer_email || "", want_script: r.data.add_script ? "Yes (already added)" : "" });
        if (r.data.already_submitted) setDone(true);
      })
      .catch((e) => setError(apiErr(e.response?.data?.detail) || "This link is invalid."));
  }, [token]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/questionnaire/${token}`, values);
      setDone(true);
      window.scrollTo({ top: 0 });
      toast.success("All done — thank you!");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not submit.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]";

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain">
      <header className="glass sticky top-0 z-40"><div className="mx-auto max-w-3xl px-4 py-4"><Logo /></div></header>
      <div className="mx-auto max-w-3xl px-4 py-10">
        {error ? (
          <div className="card-surface rounded-2xl p-10 text-center">
            <h1 className="font-display text-2xl">Hmm, that link didn't work</h1>
            <p className="mt-3 text-white/60">{error}</p>
          </div>
        ) : !meta ? (
          <div className="grid place-items-center py-24 text-white/50"><Loader2 className="animate-spin" /></div>
        ) : done ? (
          <div className="card-surface rounded-2xl p-10 text-center gold-glow">
            <CheckCircle2 className="mx-auto text-[#D4AF37]" size={52} />
            <h1 className="mt-5 font-display text-3xl">Thank you!</h1>
            <p className="mt-3 text-white/70">We've got everything we need for order <span className="text-[#D4AF37]">{meta.order_ref}</span>. Your plan is now being made and we'll email you when it's ready.</p>
            <GoldButton as="link" to="/" className="mt-8">Back to home</GoldButton>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest"><ClipboardList size={16} /> Content Plan Questionnaire</div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl">Let's make it perfect for you</h1>
            <p className="mt-3 text-white/60">Order <span className="text-[#D4AF37]">{meta.order_ref}</span> · {meta.product_name}. The more you share, the better your plan will be.</p>
            <form onSubmit={submit} data-testid="questionnaire-form" className="mt-8 card-surface rounded-2xl p-6 sm:p-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {FIELDS.map((f) => (
                  <div key={f.name} className={f.type === "textarea" || f.full ? "sm:col-span-2" : ""}>
                    <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">{f.label}{f.required && " *"}</label>
                    {f.type === "textarea" ? (
                      <textarea rows={3} required={f.required} placeholder={f.placeholder} data-testid={`q-${f.name}`}
                        value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className={inputCls} />
                    ) : f.type === "select" ? (
                      <select required={f.required} data-testid={`q-${f.name}`} value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className={inputCls}>
                        <option value="">Select...</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type || "text"} required={f.required} placeholder={f.placeholder} data-testid={`q-${f.name}`}
                        value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className={inputCls} />
                    )}
                  </div>
                ))}
              </div>
              <GoldButton type="submit" disabled={loading} data-testid="questionnaire-submit" className="w-full">
                {loading ? "Submitting..." : "Submit my answers"}
              </GoldButton>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
